import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ROUTES } from 'src/constants/routes.const';
import { useLanguagePairContext } from 'src/context/languagePair';
import { getHref } from 'src/utils/getHref';
import { getLearnableSlugTemplate } from 'src/utils/urlSlugTemplate';

type Id = number | string;

export type Query = { [index: string]: number | string };

export type UseHref = {
  (url: {
    pathname: typeof ROUTES.immersiveScenario;
    query: Query & {
      topicId: Id;
      topicTitleSlug: string;
      scenarioId: Id;
      scenarioTitleSlug: string;
      accountUrl: string;
    };
  }): ReturnType<typeof getHref>;

  (url: {
    pathname: typeof ROUTES.phrasebook.learnable;
    query: Query & {
      learnableId: Id;
      learnableSourceValueSlug: string;
    };
  }): ReturnType<typeof getHref>;

  (url: {
    pathname: typeof ROUTES.phrasebook.allLearnables;
    query?: Query & {
      page?: number;
      search?: string;
    };
  }): ReturnType<typeof getHref>;

  (url: {
    pathname: typeof ROUTES.campaigns.musicians.musician;
    query: Query & {
      campaignId: string;
    };
  }): ReturnType<typeof getHref>;

  (url: {
    pathname: typeof ROUTES.campaigns.musicians.topicOfTopic;
    query?: Query;
  }): ReturnType<typeof getHref>;

  (url: { pathname: typeof ROUTES.course; query?: Query }): ReturnType<typeof getHref>;

  (url: {
    pathname: typeof ROUTES.topic;
    query: Query & {
      topicId: Id;
      topicTitleSlug: string;
    };
  }): ReturnType<typeof getHref>;

  (url: {
    pathname: typeof ROUTES.scenario;
    query: Query & {
      topicId: Id;
      topicTitleSlug: string;
      scenarioId: Id;
      scenarioTitleSlug: string;
    };
  }): ReturnType<typeof getHref>;

  (url: {
    pathname: typeof ROUTES.tag;
    query: Query & {
      tagNameSlug: string;
    };
  }): ReturnType<typeof getHref>;
};

export const useHref: UseHref = url => {
  const intl = useIntl();

  const { sourceLanguageLocale, targetLanguageLocale } = useLanguagePairContext();

  return useMemo(() => {
    switch (url.pathname) {
      case ROUTES.immersiveScenario: {
        return getHref({
          pathname: url.pathname,
          query: {
            ...url.query,
            sourceLanguageLocale,
            targetLanguageLocale,
          },
        });
      }

      case ROUTES.phrasebook.learnable: {
        return getHref({
          pathname: url.pathname,
          query: {
            ...url.query,
            ...getLearnableSlugTemplate(intl.messages, targetLanguageLocale),
            sourceLanguageLocale,
            targetLanguageLocale,
          },
        });
      }

      case ROUTES.phrasebook.allLearnables: {
        return getHref({
          pathname: url.pathname,
          query: {
            ...url.query,
            sourceLanguageLocale,
            targetLanguageLocale,
          },
        });
      }

      case ROUTES.campaigns.musicians.musician: {
        return getHref({
          pathname: url.pathname,
          query: {
            sourceLanguageLocale,
            targetLanguageLocale,
            ...url.query,
          },
        });
      }

      case ROUTES.campaigns.musicians.topicOfTopic: {
        return getHref({
          pathname: url.pathname,
          query: {
            sourceLanguageLocale,
            targetLanguageLocale,
            ...url.query,
          },
        });
      }

      case ROUTES.course: {
        return getHref({
          pathname: url.pathname,
          query: {
            ...url.query,
            sourceLanguageLocale,
            targetLanguageLocale,
          },
        });
      }

      case ROUTES.topic: {
        const { topicTitle, ...query } = url.query;

        return getHref({
          pathname: url.pathname,
          query: {
            ...query,
            sourceLanguageLocale,
            targetLanguageLocale,
            topicTitle,
          },
        });
      }

      case ROUTES.scenario: {
        const { scenarioTitle, ...query } = url.query;

        return getHref({
          pathname: url.pathname,
          query: {
            ...query,
            sourceLanguageLocale,
            targetLanguageLocale,
            scenarioTitle,
          },
        });
      }

      case ROUTES.tag: {
        return getHref({
          pathname: url.pathname,
          query: {
            ...url.query,
            sourceLanguageLocale,
            targetLanguageLocale,
          },
        });
      }
    }
  }, [intl.messages, sourceLanguageLocale, targetLanguageLocale, url]);
};
